"use client";

import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { AnimatePresence, motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CHAUFFEUR_HUB } from "./hubDict";

type FaqSectionProps = { locale?: Locale };

const FaqSection = ({ locale = "fr" }: FaqSectionProps): JSX.Element => {
  const d = tr(CHAUFFEUR_HUB, locale).faq;
  const [openIndex, setOpenIndex] = useState<number>(0);

  return (
    <section className="bg-white py-16 text-black">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
        >
          {d.eyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-6 font-space-age text-xl tracking-[0.2em]"
        >
          {d.heading}
        </motion.h2>
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-3">
            {d.items.map((f, index) => {
              const isOpen = openIndex === index;
              return (
                <motion.article
                  key={f.q}
                  layout
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.35, delay: index * 0.04 }}
                  whileHover={{ y: -2 }}
                  className="rounded-2xl border border-black/10 bg-black/[0.02] transition hover:border-gold-500/35"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-start"
                    aria-expanded={isOpen}
                  >
                    <span className="text-sm font-semibold text-black">
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold-400/40 bg-gold-100/40 text-gold-700"
                    >
                      <Icon icon="mdi:chevron-down" className="h-4 w-4" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false} mode="wait">
                    {isOpen && (
                      <motion.div
                        key="faq-content"
                        layout
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          height: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                          opacity: { duration: 0.2, ease: "easeOut" },
                        }}
                        className="overflow-hidden border-t border-black/10"
                      >
                        <motion.p
                          initial={false}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -4, opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="px-5 py-4 text-sm text-black/80"
                        >
                          {f.a}
                        </motion.p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              );
            })}
          </div>
          <motion.aside
            initial={false}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
            whileHover={{ y: -3 }}
            className="rounded-2xl border border-gold-400/40 bg-gradient-to-b from-gold-200/25 to-transparent p-6"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-gold-800">
              {d.asideEyebrow}
            </p>
            <h3 className="mt-3 text-lg font-semibold">{d.asideTitle}</h3>
            <p className="mt-2 text-sm text-black/75">{d.asideText}</p>
            <ul className="mt-4 space-y-2 text-sm text-black/80">
              {d.asideBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
